import { SlidersHorizontal, Layers, FileSearch, Users2, FileDown } from "lucide-react";

const FEATURES = [
  {
    icon: SlidersHorizontal,
    title: "Custom Competency Matrix",
    desc: "Define your own weighted competencies per job — weights always validated to total 100% before you save.",
    tint: "bg-sky-100 text-blue-800 dark:bg-blue-950/50 dark:text-sky-300",
    span: "sm:col-span-2",
  },
  {
    icon: Layers,
    title: "Multi-Batch Evaluation",
    desc: "Upload more resumes anytime — new results land in a fresh, collapsible Batch 2, Batch 3 table. Nothing ever gets overwritten.",
    tint: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  },
  {
    icon: FileSearch,
    title: "Deep Evaluation Reports",
    desc: "Evidence-based competency scores, strengths, gaps, interview questions and a hiring verdict for every candidate.",
    tint: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  },
  {
    icon: Users2,
    title: "Multi-Tenant Workspaces",
    desc: "Invite your hiring team with Admin or Member roles. Everyone sees the same live ranking board.",
    tint: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  },
  {
    icon: FileDown,
    title: "One-Click Export",
    desc: "Export any batch's rankings to CSV, or download a polished PDF report per candidate — ready to share.",
    tint: "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
    span: "sm:col-span-2",
  },
];

export default function LandingFeatures() {
  return (
    <section id="features" className="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-xl text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-primary">Everything you need</span>
          <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Built for high-volume hiring teams</h2>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2" data-testid="landing-features-grid">
          {FEATURES.map((f, idx) => (
            <div
              key={f.title}
              className={`group rounded-2xl border border-border/70 bg-card p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg sm:p-8 glossy-card glossy-sheen ${f.span || ""}`}
              style={{ animationDelay: `${idx * 70}ms` }}
              data-testid={`landing-feature-card-${idx}`}
            >
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${f.tint}`}>
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 grid items-center gap-10 sm:grid-cols-2" data-testid="landing-report-showcase">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-primary">Deep evaluation reports</span>
            <h3 className="mt-3 font-heading text-xl font-bold tracking-tight sm:text-2xl">
              See exactly why a candidate scored the way they did
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Every candidate gets a full breakdown — competency-by-competency scores, strengths, gaps and a hiring
              verdict — viewable instantly or downloaded as a clean, shareable PDF report.
            </p>
          </div>
          <div className="rounded-2xl border border-border/80 bg-card p-2.5 shadow-lg shadow-primary/5 glossy-card glossy-sheen">
            <img
              src="/images/report-modal.jpg"
              alt="Sample hireqix candidate evaluation report with competency scores, strengths and hiring verdict"
              className="w-full rounded-xl object-cover"
              data-testid="landing-report-showcase-image"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
